import { POPUP_OPENED, PopupProps, PopupSize } from './types';
import { usePopupManager } from 'ui-builder';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEventsPublisher } from 'event-bus';
import useWindowDimensionsGetter from './windowDimensionsGetter';
import { X } from 'lucide-react';
import { cn } from 'shadcn-ui';

export default function ShadcnPopupLayoutWeb(props: PopupProps) {
  const popupManager = usePopupManager();

  const ref = useRef<HTMLDivElement>(null);

  const overlayRef = useRef<HTMLDivElement>(null);

  const eventsPublisher = useEventsPublisher();

  const {
    height: windowHeight,
  } = useWindowDimensionsGetter();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = '';
    };
  }, []);

  function centerPopup() {
    if (props.isCentered) {
      const height = ref.current?.clientHeight;

      if (height && ref.current) {
        ref.current.style.marginTop = `${-(height / 2)}px`;
      }
    }
  }

  useEffect(() => {
    if (props.isCentered) {
      centerPopup();
    }
  }, [ref.current?.clientHeight]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      centerPopup();
    });
    return () => clearTimeout(timeoutId);
  }, []);

  const [clickedInOverlay, setClickedInOverlay] = useState<boolean>(false);

  useEffect(() => {
    if (props.popupId) {
      eventsPublisher.publish(POPUP_OPENED, {
        id: props.popupId,
      });
    }
  }, []);

  useEffect(() => {
    if (props.isBig && ref.current) {
      ref.current.style.height = `${Math.max(windowHeight - 100, 800)}px`;
    }
  }, [windowHeight]);

  const handleMouseDown = useCallback((event: MouseEvent) => {
    if (overlayRef.current === event.target) {
      setClickedInOverlay(true);
    }
  }, [ref.current]);

  const handleMouseUp = useCallback(() => {
    setClickedInOverlay(false);
  }, []);

  useEffect(() => {
    if (ref.current) {
      document.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    } else {
      return undefined;
    }
  }, [ref.current]);

  /* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
  return (
    <div
      className={cn(
        'fixed inset-0 z-[200] bg-black/60 overflow-hidden',
        props.isCentered
          ? 'flex items-center justify-center'
          : 'flex justify-center items-start pt-12',
      )}
      ref={overlayRef}
      onMouseUp={(event) => {
        if (event.target === overlayRef.current && clickedInOverlay && props.isClosable) {
          popupManager.closePopup();
        }
        setClickedInOverlay(false);
      }}
      data-key="popup"
      data-testid={props.testId || 'popup'}
    >
      <div
        ref={ref}
        className={cn(
          'w-[67rem] bg-white z-[1901] rounded-lg flex flex-col',
          props.isCentered ? 'max-h-[90vh]' : 'max-h-[calc(100vh-6rem)] self-start',
          props.isBig && 'w-[95%] 2xl:w-[160rem]',
          props.size === PopupSize.WIDE && 'w-[80rem]',
          props.className,
        )}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="relative px-5 py-4 border-b border-gray-200 shrink-0">
          <span className="text-xl font-bold uppercase text-gray-800 tracking-tight">
            {props.title}
          </span>
          {props.isClosable && (
            <button
              type="button"
              className="absolute top-4 right-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
              onClick={() => popupManager.closePopup()}
              data-testid="popup-close-button"
            >
              <X className="size-4 text-gray-400" />
            </button>
          )}
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4" data-testid="popup-content">
          {props.content}
        </div>
        {props.buttons && (
          <div className="px-5 py-4 border-t border-gray-200 shrink-0 flex justify-end gap-3">
            {props.buttons}
          </div>
        )}
      </div>
    </div>
  );
}
